import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/shadcn/skeleton';
import communityService, { type MabarParticipant } from '@/services/communityService';
import padelService from '@/services/padelService';
import TambahPadelGameForm from '@/components/community/padel/TambahPadelGameForm';
import PadelSubpageHeader from '@/components/community/padel/PadelSubpageHeader';
import PadelFeedbackDialog from '@/components/community/padel/PadelFeedbackDialog';
import {
  buildGeneratePayload,
  createDefaultTambahPadelGameForm,
  generateSuccessCopy,
  type TambahPadelGameFormState,
} from '@/components/community/padel/padelGenerateUtils';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import showToast from '@/components/ui/Toast';

export default function TambahPadelGamePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isClient = isCustomerAppPath(useLocation().pathname);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [courts, setCourts] = useState(createDefaultTambahPadelGameForm().numCourts);
  const backPath =
    isClient && slug
      ? clientPaths.event.padel(slug)
      : slug
        ? hostPaths.mabar.padel(slug)
        : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backPath);
  };

  useEffect(() => {
    if (!slug) return;
    communityService
      .getEventParticipants(slug, { status: 'confirmed', limit: 200 })
      .then((partRes) => setParticipants(partRes.participants ?? []))
      .catch((err: unknown) => setErrorMessage(getApiErrorMessage(err, 'Gagal memuat pemain')))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (form: TambahPadelGameFormState) => {
    if (!slug) return;
    setSubmitting(true);
    try {
      await padelService.generateSession(slug, buildGeneratePayload(form));
      const copy = generateSuccessCopy(form, participants.length);
      showToast.success(copy.message);
      navigate(backPath);
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal membuat jadwal'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const subtitle = loading
      ? 'Memuat data pemain...'
      : `${participants.length} pemain · ganda · ${courts} lapangan`;

    const broadcast = () => {
      window.dispatchEvent(
        new CustomEvent('sporto-topbar-subtitle', {
          detail: { subtitle },
        })
      );
    };

    broadcast();
    window.addEventListener('sporto-request-topbar-subtitle', broadcast);

    return () => {
      window.removeEventListener('sporto-request-topbar-subtitle', broadcast);
      window.dispatchEvent(
        new CustomEvent('sporto-topbar-subtitle', {
          detail: { subtitle: null },
        })
      );
    };
  }, [loading, participants.length, courts]);

  if (loading) {
    return (
      <div className="space-y-3 px-4 py-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-4">
      {/* Desktop Navigation Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:block">
        <PadelSubpageHeader
          title="Tambah Game Padel"
          subtitle={`${participants.length} pemain · ganda · ${courts} lapangan`}
          onBack={goBack}
        />
      </div>
      <TambahPadelGameForm
        confirmedCount={participants.length}
        participants={participants.map((p) => ({ id: p.id, name: p.name }))}
        submitting={submitting}
        onSubmit={(form) => void submit(form)}
        onFormChange={(form) => setCourts(form.numCourts)}
        onInsufficientClose={goBack}
      />
      <PadelFeedbackDialog
        open={Boolean(errorMessage)}
        icon="mdi:alert-circle-outline"
        title="Gagal membuat jadwal"
        message={errorMessage}
        variant="error"
        onClose={() => setErrorMessage('')}
      />
    </div>
  );
}
